import { useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import type { ShopReview } from '@popcart/core/v1';
import { api } from '../../api';
import { Loading, StatusPill, useTitle } from '../../ui/bits';
import { Scene, SceneError } from '../../catalog/art';
import { Icon, PopMark } from '../../ui/Icon';
import { useToast } from '../../ui/toast';
import { Fig } from '../../ui/viz';
import { useShop } from '../ShopLayout';
import {
  MAX_REPLY,
  avgText,
  canReply,
  charsLeft,
  countText,
  dayText,
  itemsText,
  marks,
  replaceReview,
  replyError,
  replyProblem,
  reviewActions,
  starBars,
  starsLabel,
  unrepliedCount,
  type ReviewSummary,
} from './lib/reviews.js';
import { reviewKeys } from './keys';
import './reviews.css';

type ReviewsPage = { summary: ReviewSummary; page: { items: ShopReview[]; nextCursor: string | null } };

/** /shop/:shopId/reviews — what customers said, and one reply per review. */
export function ShopReviewsPage() {
  useTitle('Reviews');
  const { shopId } = useShop();
  const actions = useMemo(() => reviewActions(api, shopId), [shopId]);
  const reviews = useInfiniteQuery({
    queryKey: reviewKeys.list(shopId),
    queryFn: ({ pageParam }) => actions.page(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.page.nextCursor ?? undefined,
    enabled: !!shopId,
  });
  const pages = reviews.data?.pages ?? [];
  const summary = pages[0]?.summary;
  const list = useMemo(() => pages.flatMap((p) => p.page.items), [pages]);
  const waiting = unrepliedCount(list);

  return (
    <>
      <header className="dv-head">
        <div className="stack s4">
          <h1>Reviews</h1>
          <span className="dv-live">{summary ? `${countText(summary.count)}${waiting ? ` · ${waiting} waiting for a reply` : ''}` : 'What customers said'}</span>
        </div>
      </header>

      {reviews.isError && !reviews.data ? (
        <section className="card">
          <SceneError error={reviews.error} onRetry={() => void reviews.refetch()} />
        </section>
      ) : !summary ? (
        <Loading label="Loading reviews" rows={3} />
      ) : summary.count === 0 && list.length === 0 ? (
        <section className="card">
          <Scene scene="delivered" title="No reviews yet">
            Customers rate an order after it reaches them. Their words land here, and you can answer each one.
          </Scene>
        </section>
      ) : (
        <div className="rv-grid">
          <SummaryCard s={summary} waiting={waiting} />
          <section className="card dv-card dv-rise" style={{ ['--i' as string]: 1 }} aria-labelledby="rv-list-h">
            <div className="dv-card-h">
              <h2 id="rv-list-h">Every review</h2>
              {waiting ? <span className="dv-chip">{waiting} to answer</span> : null}
            </div>
            <ul className="rv-list" aria-label="Reviews">
              {list.map((r) => (
                <li key={r.ratingId}>
                  <ReviewItem shopId={shopId} r={r} />
                </li>
              ))}
            </ul>
            {reviews.hasNextPage ? (
              <div className="row rv-more">
                <button type="button" className="btn btn-quiet" onClick={() => void reviews.fetchNextPage()} disabled={reviews.isFetchingNextPage} aria-busy={reviews.isFetchingNextPage || undefined}>
                  {reviews.isFetchingNextPage ? 'Loading…' : 'Show older reviews'}
                </button>
              </div>
            ) : null}
          </section>
        </div>
      )}
    </>
  );
}

/** Five pop marks (the customer app rates with them): filled ones sit on Pop Yellow. */
function PopStars({ stars, size = 'md' }: { stars: number; size?: 'sm' | 'md' }) {
  return (
    <span className={`rv-stars ${size}`} role="img" aria-label={starsLabel(stars)}>
      {marks(stars).map((on, i) => (
        <span key={i} className={`rv-mark${on ? ' on' : ''}`} aria-hidden="true">
          {on ? <PopMark size={size === 'sm' ? 12 : 16} tone="ink" /> : null}
        </span>
      ))}
    </span>
  );
}

function SummaryCard({ s, waiting }: { s: ReviewSummary; waiting: number }) {
  return (
    <section className="card dv-card rv-summary dv-rise" aria-labelledby="rv-sum-h">
      <h2 id="rv-sum-h" className="sr-only">
        Rating
      </h2>
      <div className="rv-head">
        <span className="rv-avg" role="img" aria-label={`Average ${avgText(s.avg)} out of 5`}>
          <Fig text={avgText(s.avg)} size="xl" />
        </span>
        <span className="stack s8">
          <PopStars stars={s.avg ?? 0} />
          <span className="dv-sub">{countText(s.count)}</span>
        </span>
      </div>
      <ul className="rv-bars" aria-label="Reviews by stars">
        {starBars(s).map((b, j) => (
          <li key={b.stars} className="rv-barrow" style={{ ['--j' as string]: j } as CSSProperties}>
            <span className="rv-s">
              {b.stars}
              <span className="rv-dot" aria-hidden="true" />
            </span>
            <span className="rv-bar" role="img" aria-label={`${b.stars} stars: ${b.n}`}>
              <i className={b.n ? '' : 'empty'} style={{ width: `${b.pct}%` } as CSSProperties} />
            </span>
            <span className="rv-n">{b.n}</span>
          </li>
        ))}
      </ul>
      <div className="rv-foot">
        <span className="dv-label">To answer</span>
        <Fig text={String(waiting)} size="md" />
      </div>
    </section>
  );
}

function ReviewItem({ shopId, r }: { shopId: string; r: ShopReview }) {
  return (
    <article className="rv-item" aria-label={`${r.customerFirstName}, ${starsLabel(r.stars)}`}>
      <div className="row between wrap">
        <span className="row wrap">
          <PopStars stars={r.stars} size="sm" />
          <b className="rv-name">{r.customerFirstName}</b>
        </span>
        <span className="dv-sub">
          <span className="ph-code rv-code">{r.orderCode}</span> · {dayText(r.createdAt)}
        </span>
      </div>
      {r.comment ? <p className="rv-comment">{r.comment}</p> : <p className="dv-sub">Just the stars</p>}
      {r.items.length ? (
        <span className="row dv-sub rv-items">
          <Icon name="bag" size="sm" />
          {itemsText(r.items)}
        </span>
      ) : null}
      {r.reply ? (
        <div className="rv-reply">
          <span className="dv-label">Your reply{r.repliedAt ? ` · ${dayText(r.repliedAt)}` : ''}</span>
          <p>{r.reply}</p>
        </div>
      ) : canReply(r) ? (
        <ReplyBox shopId={shopId} r={r} />
      ) : null}
    </article>
  );
}

function ReplyBox({ shopId, r }: { shopId: string; r: ShopReview }) {
  const qc = useQueryClient();
  const toast = useToast();
  const actions = useMemo(() => reviewActions(api, shopId), [shopId]);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () => actions.reply(r.ratingId, text),
    onSuccess: (updated) => {
      qc.setQueryData<InfiniteData<ReviewsPage>>(reviewKeys.list(shopId), (d) => (d ? { ...d, pages: replaceReview(d.pages, updated) } : d));
      toast('Reply posted. The customer can see it.');
    },
    onError: (e) => {
      const status = (e as { status?: number }).status;
      setErr(replyError(e as { status?: number; message?: string }));
      if (status === 409) void qc.invalidateQueries({ queryKey: reviewKeys.list(shopId) });
    },
  });
  const id = `rv-reply-${r.ratingId}`;
  if (!open) {
    return (
      <div className="row">
        <StatusPill tone="warning">No reply yet</StatusPill>
        <button type="button" className="btn btn-quiet rv-replybtn" onClick={() => setOpen(true)}>
          Reply
        </button>
      </div>
    );
  }
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const p = replyProblem(text);
    setErr(p);
    if (!p) send.mutate();
  };
  const left = charsLeft(text);
  return (
    <form className="stack rv-form" onSubmit={onSubmit} noValidate>
      <div className="field">
        <label htmlFor={id}>Reply to {r.customerFirstName}</label>
        <textarea
          id={id}
          className="input"
          value={text}
          maxLength={MAX_REPLY}
          aria-invalid={err ? true : undefined}
          aria-describedby={err ? `${id}-err` : `${id}-help`}
          onChange={(e) => {
            setText(e.target.value);
            if (err) setErr(null);
          }}
        />
        {err ? (
          <span id={`${id}-err`} className="pill st-error err-pill" role="alert">
            {err}
          </span>
        ) : (
          <span id={`${id}-help`} className="help">
            Public · one per review · {left} left
          </span>
        )}
      </div>
      <div className="row">
        <button type="button" className="btn btn-quiet" onClick={() => setOpen(false)} disabled={send.isPending}>
          Not now
        </button>
        <button type="submit" className="btn btn-secondary" disabled={send.isPending} aria-busy={send.isPending || undefined}>
          {send.isPending ? 'Posting…' : 'Post reply'}
        </button>
      </div>
    </form>
  );
}
